import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  DollarSign,
  TrendingUp,
  Filter,
  Download,
  Calendar,
  User,
  Search
} from 'lucide-react';
import api from '../../services/api';

const TransactionsManagement = () => {
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const limit = 20;

  // Query para obtener todas las transacciones
  const { data, isLoading, error } = useQuery(
    ['all-transactions', page, typeFilter],
    () => api.get('/transactions', {
      params: { page, limit, type: typeFilter }
    }).then(res => res.data),
    {
      keepPreviousData: true,
      staleTime: 30000
    }
  );

  const formatCurrency = (amount) => {
    const value = Number(amount || 0);
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeConfig = (type) => {
    const configs = {
      bet: {
        label: 'Apuesta',
        color: 'text-blue-600 dark:text-blue-400',
        bg: 'bg-blue-100 dark:bg-blue-900/30',
        icon: ArrowDownCircle
      },
      win: {
        label: 'Ganancia',
        color: 'text-green-600 dark:text-green-400',
        bg: 'bg-green-100 dark:bg-green-900/30',
        icon: TrendingUp
      },
      initial_deposit: {
        label: 'Depósito Inicial',
        color: 'text-purple-600 dark:text-purple-400',
        bg: 'bg-purple-100 dark:bg-purple-900/30',
        icon: ArrowUpCircle
      },
      withdrawal: {
        label: 'Retiro',
        color: 'text-orange-600 dark:text-orange-400',
        bg: 'bg-orange-100 dark:bg-orange-900/30',
        icon: ArrowDownCircle
      },
      admin_fee: {
        label: 'Comisión Admin',
        color: 'text-yellow-600 dark:text-yellow-400',
        bg: 'bg-yellow-100 dark:bg-yellow-900/30',
        icon: DollarSign
      },
      elimination: {
        label: 'Eliminación',
        color: 'text-red-600 dark:text-red-400',
        bg: 'bg-red-100 dark:bg-red-900/30',
        icon: ArrowDownCircle
      },
      reactivation: {
        label: 'Reactivación',
        color: 'text-green-600 dark:text-green-400',
        bg: 'bg-green-100 dark:bg-green-900/30',
        icon: ArrowUpCircle
      }
    };

    return configs[type] || {
      label: type,
      color: 'text-gray-600 dark:text-gray-400',
      bg: 'bg-gray-100 dark:bg-gray-900/30',
      icon: DollarSign
    };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8 text-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">
          Error al cargar transacciones: {error.message}
        </p>
      </div>
    );
  }

  const transactions = data?.transactions || [];
  const pagination = data?.pagination || {};

  // Filtrar transacciones localmente por término de búsqueda
  const filteredTransactions = transactions.filter(transaction => {
    if (!searchTerm) return true;

    const term = searchTerm.toLowerCase();
    const userName = (transaction.user_name || '').toLowerCase();
    const eventName = (transaction.event_name || '').toLowerCase();
    const description = (transaction.description || '').toLowerCase();
    const type = (transaction.type || '').toLowerCase();

    return userName.includes(term) ||
           eventName.includes(term) ||
           description.includes(term) ||
           type.includes(term);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Gestión de Transacciones
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Historial completo de todas las transacciones del sistema
          </p>
        </div>

        <button
          onClick={() => {/* TODO: Implementar exportación */}}
          className="btn-secondary"
        >
          <Download className="w-4 h-4 mr-2" />
          Exportar
        </button>
      </div>

      {/* Filtros */}
      <div className="card p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Búsqueda */}
          <div className="relative">
            <Search className="absolute left-3 top-3.5 text-gray-400 w-5 h-5 pointer-events-none" />
            <input
              type="text"
              placeholder="Buscar por cliente, evento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2.5 pl-10 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Filtro por tipo */}
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
            className="w-full px-4 py-2.5 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all" className="bg-white dark:bg-dark-800 text-gray-900 dark:text-gray-100">Todos los tipos</option>
            <option value="bet" className="bg-white dark:bg-dark-800 text-gray-900 dark:text-gray-100">Apuestas</option>
            <option value="win" className="bg-white dark:bg-dark-800 text-gray-900 dark:text-gray-100">Ganancias</option>
            <option value="initial_deposit" className="bg-white dark:bg-dark-800 text-gray-900 dark:text-gray-100">Depósitos Iniciales</option>
            <option value="withdrawal" className="bg-white dark:bg-dark-800 text-gray-900 dark:text-gray-100">Retiros</option>
            <option value="admin_fee" className="bg-white dark:bg-dark-800 text-gray-900 dark:text-gray-100">Comisiones Admin</option>
            <option value="elimination" className="bg-white dark:bg-dark-800 text-gray-900 dark:text-gray-100">Eliminaciones</option>
            <option value="reactivation" className="bg-white dark:bg-dark-800 text-gray-900 dark:text-gray-100">Reactivaciones</option>
          </select>

          {/* TODO: Filtro por fecha */}
          <button className="btn-secondary">
            <Calendar className="w-4 h-4 mr-2" />
            Filtrar por fecha
          </button>
        </div>
      </div>

      {/* Tabla de Transacciones */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Descripción
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Monto
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Balance Después
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-900 divide-y divide-gray-200 dark:divide-dark-700">
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    {searchTerm
                      ? `No se encontraron transacciones con "${searchTerm}"`
                      : 'No hay transacciones registradas'
                    }
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((transaction) => {
                  const typeConfig = getTypeConfig(transaction.type);
                  const Icon = typeConfig.icon;

                  return (
                    <motion.tr
                      key={transaction.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {formatDate(transaction.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900 dark:text-gray-100">
                            {transaction.user_name || `Usuario #${transaction.user_id}`}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeConfig.bg} ${typeConfig.color}`}>
                          <Icon className="w-3 h-3 mr-1" />
                          {typeConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 max-w-md truncate">
                        {transaction.description || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <span className={`font-medium ${
                          Number(transaction.amount) >= 0
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {formatCurrency(transaction.amount)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                        {formatCurrency(transaction.balance_after)}
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {searchTerm ? (
                  <>Mostrando {filteredTransactions.length} de {transactions.length} transacciones (filtrado)</>
                ) : (
                  <>Mostrando página {pagination.currentPage} de {pagination.totalPages} ({pagination.totalItems} transacciones en total)</>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionsManagement;
